import { useEffect, useState } from 'react';
import apiClient from '../../../api/apiClient';
import cyclesAPI from '../../../api/cycles';
import { buildClockMarket, symbolKey } from '../clock/clockModel';
import { getActiveAccountId, onAccountScopedClear } from '../../account/accountRegistry';

// Everything the Desk's "Today for {symbol}" panel shows (DWLF-300), from the API's own reads:
// GET /intelligence/{symbol} (price, regime, weekly cycle state, swing structure, active events),
// the stored weekly window + screener stage (through the Cycle Clock's model, so the Desk, the Clock
// and Today place a market identically), and any planned trade awaiting a decision.

// The screener is the account's watchlist screen, refreshed through the day: shared across symbol
// switches for a few minutes, per account, dropped on sign-out (charter invariant 3).
const SCREENER_MS = 5 * 60 * 1000;
let screenerCache = { key: null, at: 0, promise: null };
onAccountScopedClear(() => { screenerCache = { key: null, at: 0, promise: null }; });
const screener = () => {
  const key = getActiveAccountId() || 'anon';
  if (screenerCache.key !== key || Date.now() - screenerCache.at > SCREENER_MS || !screenerCache.promise) {
    const promise = apiClient.get('/screener/cycle-setups').then((r) => r.data).catch(() => { screenerCache.promise = null; return null; });
    screenerCache = { key, at: Date.now(), promise };
  }
  return screenerCache.promise;
};

export default function useDeskSymbol(symbol) {
  const [state, setState] = useState({ loading: true });
  const key = symbolKey(symbol);

  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    setState({ loading: true });
    const settled = (p) => p.then((value) => ({ ok: true, value }), () => ({ ok: false }));
    // The Desk reads the weekly LOW cycle only, and its words say so ("Last confirmed low"); the Clock's Highs
    // mode (DWLF-552) does not reach it yet. Making it side-aware means a side here and in DeskPanel's labels.
    Promise.all([
      settled(apiClient.get(`/intelligence/${key}`).then((r) => r.data)),
      settled(cyclesAPI.getWindows({ symbol: key, timeframe: '1w', side: 'low' })),
      screener(),
      settled(apiClient.get('/trades', { params: { status: 'planned', limit: 50 } }).then((r) => r.data?.items || r.data?.trades || [])),
    ]).then(([intel, windows, setups, planned]) => {
      if (!live) return;
      const row = (setups?.long || []).find((r) => symbolKey(r.symbol) === key);
      setState({
        loading: false,
        intel: intel.ok ? intel.value : null,
        intelFailed: !intel.ok,
        cycle: buildClockMarket(key, windows.ok ? windows.value : null, row, Date.now(), { failed: !windows.ok }),
        pending: planned.ok ? planned.value.filter((t) => symbolKey(t.assetSymbol) === key) : [],
        pendingFailed: !planned.ok,
      });
    });
    return () => { live = false; };
  }, [key]);

  const dropDecided = (tradeId) => setState((s) => ({ ...s, pending: (s.pending || []).filter((t) => t.tradeId !== tradeId) }));
  return { ...state, dropDecided };
}
